import { useState, useEffect, useCallback } from 'react';
import { type ColumnConfig } from '@/components/collections/column-config-dialog';

const STORAGE_KEY = 'collections-queue-column-preferences';

// Default column configuration - this defines the original view
export const DEFAULT_COLUMNS: ColumnConfig[] = [
  { id: 'select', label: 'Select', visible: true, order: 0, isDefault: true, category: 'basic' },
  { id: 'priority', label: 'Priority', visible: true, order: 1, isDefault: true, category: 'status' },
  { id: 'caseId', label: 'Case ID', visible: true, order: 2, isDefault: true, category: 'basic' },
  { id: 'customerName', label: 'Customer', visible: true, order: 3, isDefault: true, category: 'basic' },
  { id: 'amount', label: 'Amount', visible: true, order: 4, isDefault: true, category: 'details' },
  { id: 'daysInCollection', label: 'Days', visible: true, order: 5, isDefault: true, category: 'dates' },
  { id: 'nextAction', label: 'Next Action', visible: true, order: 6, isDefault: true, category: 'details' },
  { id: 'nextActionDate', label: 'Action Due Date', visible: true, order: 7, isDefault: true, category: 'dates' },
  { id: 'status', label: 'Status', visible: true, order: 8, isDefault: true, category: 'status' },
  // Additional columns available from Case Details but not shown by default
  { id: 'customerId', label: 'Customer ID', visible: false, order: 9, isDefault: false, category: 'basic' },
  { id: 'customerEmail', label: 'Email', visible: false, order: 10, isDefault: false, category: 'basic' },
  { id: 'customerPhone', label: 'Phone', visible: false, order: 11, isDefault: false, category: 'basic' },
  { id: 'riskLevel', label: 'Risk Level', visible: false, order: 12, isDefault: false, category: 'status' },
  { id: 'strategy', label: 'Strategy', visible: false, order: 13, isDefault: false, category: 'details' },
  { id: 'assignedTo', label: 'Assigned To', visible: false, order: 14, isDefault: false, category: 'details' },
  { id: 'createdAt', label: 'Created Date', visible: false, order: 15, isDefault: false, category: 'dates' },
  { id: 'updatedAt', label: 'Last Updated', visible: false, order: 16, isDefault: false, category: 'dates' },
];

export function useColumnPreferences() {
  const [columns, setColumns] = useState<ColumnConfig[]>(DEFAULT_COLUMNS);
  const [isLoading, setIsLoading] = useState(true);

  // Load preferences from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as ColumnConfig[];
        // Merge with defaults to handle any new columns added in updates
        const merged = mergeWithDefaults(parsed, DEFAULT_COLUMNS);
        setColumns(merged);
      }
    } catch (error) {
      console.error('Failed to load column preferences:', error);
      // Fall back to defaults on error
      setColumns(DEFAULT_COLUMNS);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Save preferences to localStorage
  const savePreferences = useCallback((newColumns: ColumnConfig[]) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newColumns));
      setColumns(newColumns);
    } catch (error) {
      console.error('Failed to save column preferences:', error);
    }
  }, []);

  // Reset to default configuration
  const resetToDefaults = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      setColumns(DEFAULT_COLUMNS);
    } catch (error) {
      console.error('Failed to reset column preferences:', error);
    }
  }, []);

  // Get visible columns in order
  const visibleColumns = columns
    .filter((col) => col.visible)
    .sort((a, b) => a.order - b.order);

  return {
    columns,
    visibleColumns,
    isLoading,
    savePreferences,
    resetToDefaults,
  };
}

// Helper function to merge stored preferences with defaults
// This ensures new columns are added when the app is updated
function mergeWithDefaults(
  stored: ColumnConfig[],
  defaults: ColumnConfig[]
): ColumnConfig[] {
  const storedMap = new Map(stored.map((col) => [col.id, col]));
  
  return defaults.map((defaultCol) => {
    const storedCol = storedMap.get(defaultCol.id);
    if (storedCol) {
      // Use stored preferences but keep default metadata
      return {
        ...defaultCol,
        visible: storedCol.visible,
        order: storedCol.order,
      };
    }
    // New column not in stored preferences, use default
    return defaultCol;
  });
}
